import { useState } from 'react';

import { IconEdit, IconPrinter } from '../../components/layout/icons';
import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { Modal } from '../../components/ui/Modal';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { openAuthenticatedRoute } from '../../lib/auth';
import { confirmUpdate } from '../../lib/confirm-service';
import { useTableSort } from '../../lib/table-sort';
import { useToast } from '../../lib/toast';
import type { BillableAdmission } from './billable-admission';
import { useIpdBills, useUpdateIpdBillReceipt } from './ipd-daycare.queries';
import { currency, formatIpdDateTime, toDateTimeInput } from './ipd-daycare.utils';
import type { IpdBillPayment, IpdBillPaymentMode } from './types';

const paymentModes: IpdBillPaymentMode[] = ['Cash', 'UPI', 'Transfer to Bank Account', 'Cheque', 'Other', 'Online'];

export const IpdAllReceiptList = ({ admission, printPath = `/ipd-daycare/${admission.id}/print-receipt` }: {
  admission: BillableAdmission;
  printPath?: string;
}) => {
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const query = useIpdBills(admission.id, sortBy, sortOrder);
  const [editing, setEditing] = useState<{ billId: string; payment: IpdBillPayment } | null>(null);
  const bills = query.data ?? [];
  const receipts = bills.flatMap((bill) => bill.payments.map((payment) => ({ bill, payment })));
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState title="Unable to load receipts" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} />;
  const totalReceived = receipts.reduce((sum, receipt) => sum + receipt.payment.amount, 0);

  return (
    <>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>All Receipt — {admission.patientSnapshot.name}</span></div>
        <p>{admission.admissionLabel} · {receipts.length} receipt{receipts.length === 1 ? '' : 's'}</p>
      </header>
      <section className="opd-bill-subsection">
        <div className="opd-bill-subsection-head"><h3>Receipts across all bills</h3><span>Total Received <strong>{currency(totalReceived)}</strong></span></div>
        {receipts.length === 0 ? (
          <p className="opd-bill-empty-note">No payments have been recorded for this admission.</p>
        ) : (
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><SortableTh control={tableSort} column="billId">Bill No</SortableTh><SortableTh control={tableSort} column="billDate">Date</SortableTh><SortableTh control={tableSort} column="mode">Mode</SortableTh><SortableTh control={tableSort} column="bank">Bank</SortableTh><SortableTh control={tableSort} column="receivedBy">Received By</SortableTh><SortableTh control={tableSort} column="amount">Amount</SortableTh><th>Action</th></tr></thead>
              <tbody>
                {receipts.map((receipt, index) => (
                  <tr key={receipt.payment.id}>
                    <td>{index + 1}</td>
                    <td>{receipt.bill.billNumber}</td>
                    <td>{formatIpdDateTime(receipt.payment.date)}</td>
                    <td>{receipt.payment.mode}</td>
                    <td>{receipt.payment.bankName ?? '-'}</td>
                    <td>{receipt.payment.createdBy.name}</td>
                    <td>{currency(receipt.payment.amount)}</td>
                    <td className="opd-row-action-group">
                      <button
                        type="button"
                        className="opd-row-action-icon"
                        title="Print Receipt"
                        aria-label="Print Receipt"
                        onClick={() => openAuthenticatedRoute(`${printPath}/${receipt.bill.id}/${receipt.payment.id}`)}
                      >
                        <IconPrinter />
                      </button>
                      <button
                        type="button"
                        className="opd-row-action-icon"
                        title="Edit Receipt"
                        aria-label="Edit Receipt"
                        onClick={() => setEditing({ billId: receipt.bill.id, payment: receipt.payment })}
                      >
                        <IconEdit />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <IpdEditReceiptModal editing={editing} onClose={() => setEditing(null)} />
    </>
  );
};

const IpdEditReceiptModal = ({ editing, onClose }: { editing: { billId: string; payment: IpdBillPayment } | null; onClose: () => void }) => {
  const [date, setDate] = useState('');
  const [mode, setMode] = useState<IpdBillPaymentMode>('Cash');
  const [bankName, setBankName] = useState('');
  const [amount, setAmount] = useState(0);
  const [seed, setSeed] = useState(editing);
  if (editing !== seed) {
    setSeed(editing);
    if (editing) {
      setDate(toDateTimeInput(editing.payment.date));
      setMode(editing.payment.mode);
      setBankName(editing.payment.bankName ?? '');
      setAmount(editing.payment.amount);
    }
  }
  const mutation = useUpdateIpdBillReceipt();
  const { showToast } = useToast();

  const submit = () => {
    if (!editing || amount <= 0) return;
    void confirmUpdate('this receipt').then((confirmed) => {
      if (!confirmed) return;
      mutation.mutate(
        { billId: editing.billId, paymentId: editing.payment.id, payload: { date: new Date(date).toISOString(), mode, bankName: bankName.trim() || null, amount } },
        {
          onSuccess: () => {
            showToast('Receipt updated successfully.', 'success');
            onClose();
          },
          onError: (error) => showToast(error instanceof ApiError ? error.message : 'The receipt could not be updated.', 'error'),
        },
      );
    });
  };

  return (
    <Modal open={editing !== null} onClose={() => { if (!mutation.isPending) onClose(); }} labelledBy="edit-receipt-title">
      <header>
        <div><p>Receipt</p><h2 id="edit-receipt-title">Edit Receipt</h2></div>
        <button type="button" onClick={onClose} disabled={mutation.isPending} aria-label="Close">×</button>
      </header>
      <form onSubmit={(event) => { event.preventDefault(); submit(); }}>
        <label className="field"><span>Date</span><input required type="datetime-local" value={date} onChange={(event) => setDate(event.target.value)} /></label>
        <label className="field"><span>Mode</span><select value={mode} onChange={(event) => setMode(event.target.value as IpdBillPaymentMode)}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="field"><span>Bank</span><input value={bankName} onChange={(event) => setBankName(event.target.value)} /></label>
        <label className="field"><span>Amount</span><input required type="number" min={0.01} step="0.01" value={amount} onChange={(event) => setAmount(Number(event.target.value))} /></label>
        <div className="bed-modal-actions">
          <button className="button button-secondary" type="button" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
          <button className="button button-primary" type="submit" disabled={amount <= 0 || mutation.isPending}>{mutation.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};
